import React, { useState, useRef, useEffect } from "react";
import api from "api/api";
import Select from "react-select";

// mui component
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import DialogContentText from "@mui/material/DialogContentText";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

// icon
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

//loader
import Loader from "../common/Loader";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

//Props Validations
import PropTypes from "prop-types";

const LightTooltip = styled(({ className, offset, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        PopperProps={{
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset,
                    },
                },
            ],
        }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: theme.shadows[1],
        fontSize: "0.6rem",
        width: "280px",
        border: "1px solid rgb(51, 94, 234, 0.5)",
        padding: "2px",
    },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return (
        <Slide
            direction="up"
            ref={ref}
            {...props}
        />
    );
});

const generatePointString = (scale1, scale2) => {
    if (scale1 === null || scale2 === null || scale1 === scale2) {
        return "";
    }

    const start = Math.min(scale1, scale2) + 1;
    const end = Math.max(scale1, scale2);

    const points = [];
    for (let i = start; i <= end; i++) {
        points.push(i);
    }

    if (points.length === 0) {
        return "";
    }
    if (points.length === 1) {
        return `'${points[0]}'`;
    }
    if (points.length === 2) {
        return `'${points.join("' and '")}'`;
    }
    const lastPoint = points.pop();
    return `'${points.join("', '")}', and '${lastPoint}'`;
};

function StatementsAddUpdateComponent({ title = "", type = "", tagsData = [], selectedRows = [], isOpen = false, setIsAddDialogOpen = () => {}, setIsUpdateDialogOpen = () => {}, fetchData = () => {}, dataView = {}, selectedStudyType = {}, selectedStudyTypeMethod = [] }) {
    const dispatch = useDispatch();
    const selectInputRef = useRef();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    // add dialog confirmation
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

    //add loader
    const [isLoading, setIsLoading] = useState(false);

    //value
    const [selectedTags, setSelectedTags] = useState(null);

    // Add a ref to track if study references have been fetched
    const hasFetchedStudyRefs = useRef(false);
    const [likertWarning, setLikertWarning] = useState({ flag: false, message: "" });
    const [originalLikertScale, setOriginalLikertScale] = useState(null);

    const [tooltipContent, setTooltipContent] = useState({
        backend_variable: { flag: false, message: "" },
    });

    const [data, setData] = useState({
        backend_variable: "Claim__",
        statement: "",
        claims_tag_mapping: "",
        likert_scale: "",
    });

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    // Handle tooltip content for backend variable only
    const handleTooltipContent = (value, field) => {
        if (type !== "update") return;

        const updateObj = {};

        if (dataView.value === "1" && field === "backend_variable" && selectedRows.backend_variable !== value) {
            updateObj.backend_variable = {
                flag: true,
                message: (
                    <div>
                        <p>The backend variable name you are attempting to update is currently used in the following studies:</p>
                        <ul>
                            {selectedRows.study_reference__title.map((datum) => (
                                <li key={datum}>{datum}</li>
                            ))}
                        </ul>
                    </div>
                ),
            };
        } else if (dataView.value === 1 && field === "backend_variable") {
            updateObj.backend_variable = {
                flag: false,
                message: "",
            };
        }

        setTooltipContent((prev) => ({ ...prev, ...updateObj }));
    };

    const setUpdatedData = () => {
        if (type === "update") {
            const tags = selectedRows.claims_tag_mapping.map((datum) => ({ value: datum, label: tagsData.find((tag) => tag.value === datum).label }));
            setData({
                backend_variable: selectedRows.backend_variable,
                statement: selectedRows.statement,
                title: selectedRows.statement,
                claims_tag_mapping: tags,
                likert_scale: selectedRows.likert_scale || "",
            });
            setSelectedTags(tags);
            setOriginalLikertScale(selectedRows.likert_scale || null);
        }
    };

    useEffect(() => {
        setUpdatedData();
    }, []);

    const handleReset = () => {
        selectInputRef.current.clearValue();
        // Reset the fetch flag on reset
        hasFetchedStudyRefs.current = false;
        setLikertWarning({ flag: false, message: "" });

        if (type === "add") {
            setData({
                backend_variable: "Claim__",
                statement: "",
                claims_tag_mapping: "",
                likert_scale: "",
            });
            setSelectedTags(null);
        } else if (type === "update") {
            setUpdatedData();
            setTooltipContent({
                backend_variable: { flag: false, message: "" },
            });
        }
    };

    const handleClose = () => {
        handleReset();
        setIsAddDialogOpen(false);
        setIsUpdateDialogOpen(false);
    };

    const validateForm = () => {
        if (!data.backend_variable || data.backend_variable === "Claim__" || !data.statement || !data.claims_tag_mapping || data.claims_tag_mapping.length === 0) {
            return "Incomplete inputs fields.";
        }
        if (!data.likert_scale) {
            return "Likert Scale field is required.";
        }
        return true;
    };

    const handleLikertScaleValidation = (newValue) => {
        if (type !== "update" || dataView.value !== "1") {
            setLikertWarning({ flag: false, message: "" });
            return;
        }

        const newScale = newValue ? parseInt(newValue, 10) : null;
        const currentScale = originalLikertScale ? parseInt(originalLikertScale, 10) : null;

        if (!newScale || newScale === currentScale) {
            setLikertWarning({ flag: false, message: "" });
            return;
        }

        // Call the helper to get the dynamic list of points
        const pointsString = generatePointString(currentScale, newScale);
        if (!pointsString) {
            setLikertWarning({ flag: false, message: "" });
            return;
        }

        if (newScale < currentScale) {
            //  Decreasing the scale
            const message = (
                <div>
                    The Claims backend variable for which you are trying to update the &apos;Likert&apos;Scale&apos; as &apos;{newScale}-point&apos;, has {pointsString} data points observed in the Study_Data sheet of the following studies. This will not impact the analysis, but may mislead the
                    &apos;Likert Scale&apos; information –
                    <ul style={{ paddingLeft: "20px", marginBottom: 0, marginTop: "5px" }}>
                        {selectedRows.study_reference__title.map((study) => (
                            <li key={study}>{study}</li>
                        ))}
                    </ul>
                </div>
            );
            setLikertWarning({ flag: true, message });
        } else {
            const message = `The Claims backend variable for which you are trying to update the ‘Likert’ Scale as ‘${newScale}‑point’, doesn’t have ${pointsString} data points available across the studies. This will not impact the analysis, but may mislead the ‘Likert Scale’ information`;
            setLikertWarning({ flag: true, message });
        }
    };

    const handleInputValueChange = (selectedValue, type, fieldId) => {
        const updateField = (value) => {
            setData((prev) => ({ ...prev, [fieldId]: value }));
        };

        if (type === "dropdown") {
            const isValidDropdown = selectedValue.length !== 0 && selectedValue.every((d) => d.value && d.label.trim() !== "");
            return updateField(isValidDropdown ? selectedValue : "");
        }

        if (type === "text") {
            return updateField(selectedValue || "");
        }

        if (type === "likertScaleText") {
            const value = selectedValue.replace(/[^0-9]/g, "");
            updateField(value);
            handleLikertScaleValidation(value);
            return;
        }

        if (type === "backendVariableText") {
            const isValid = selectedValue && selectedValue !== "" && !selectedValue.includes(" ");

            if (!isValid) {
                return updateField(data[fieldId]);
            }

            // Call tooltip function when backend variable changes
            handleTooltipContent(selectedValue, "backend_variable");

            return updateField(selectedValue.startsWith("Claim__") ? selectedValue : "Claim__");
        }
    };

    const handleSave = () => {
        const validationResult = validateForm();
        if (validationResult === true) {
            const tagsMapping = data.claims_tag_mapping.map((datum) => datum.value);
            let payload = {
                ...data,
                title: data?.statement || "",
                claims_tag_mapping: tagsMapping,
                likert_scale: data.likert_scale ? parseInt(data.likert_scale, 10) : null,
            };
            const studyTypeMethods = [...new Set(selectedStudyTypeMethod.map((datum) => datum.value))];

            if (type === "add") {
                if (productCategoryId && selectedStudyType?.value && studyTypeMethods.length > 0) {
                    setIsLoading(true);
                    payload = { ...payload, product_category: productCategoryId, study_type: selectedStudyType.value, study_type_methods: studyTypeMethods };
                    try {
                        const url = process.env.REACT_APP_SERVER_PATH + `/claims-mappings/`;
                        api.post(url, payload)
                            .then(() => {
                                fetchData();
                                setIsLoading(false);
                                handleClose();
                                handleConfirmationDialogClose();
                            })
                            .catch((error) => {
                                handleError("error", error);
                                setIsLoading(false);
                                handleConfirmationDialogClose();
                            });
                    } catch (error) {
                        handleError("error", error);
                        setIsLoading(false);
                        handleConfirmationDialogClose();
                    }
                } else {
                    handleError("error", "Product category / Study type / Study type methods are not selected");
                }
            } else if (type === "update") {
                if (productCategoryId && selectedStudyType?.value && studyTypeMethods.length > 0) {
                    payload = { ...payload, product_category: productCategoryId, study_type: selectedStudyType.value, study_type_methods: studyTypeMethods };
                    try {
                        setIsLoading(true);
                        const url = process.env.REACT_APP_SERVER_PATH + `/claims-mappings/${selectedRows.id}/`;
                        api.patch(url, payload)
                            .then(() => {
                                fetchData();
                                setIsLoading(false);
                                handleClose();
                                handleConfirmationDialogClose();
                            })
                            .catch((error) => {
                                handleError("error", error);
                                setIsLoading(false);
                                handleConfirmationDialogClose();
                            });
                    } catch (error) {
                        handleError("error", error);
                        setIsLoading(false);
                        handleConfirmationDialogClose();
                    }
                } else {
                    handleError("error", "Product category / Study type / Study type methods are not selected");
                }
            }
        } else {
            handleError("error", validationResult);
        }
    };

    const handleConfirmationDialogClose = () => {
        setIsConfirmationDialogOpen(false);
    };

    return (
        <>
            {isLoading && <Loader />}
            <Dialog
                aria-labelledby="customized-dialog-title"
                open={isOpen}
                fullWidth={true}
                maxWidth={"md"}
            >
                <DialogTitle
                    style={{
                        backgroundColor: "rgb(51, 94, 234,0.1)",
                        color: "rgb(51, 94, 234)",
                        fontSize: 15,
                        textAlign: "center",
                        padding: "10px 12px",
                    }}
                    // className="liner-light-blue-background-2"
                    id="customized-dialog-title"
                >
                    {title}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 2,
                        color: "rgb(51, 94, 234) !important",
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <div className="row pb-2">
                        <div className="col-4">
                            <label className="input-label text-sm ">
                                Backend Variable :{/* ONLY BACKEND VARIABLE WARNING TOOLTIP */}
                                {tooltipContent.backend_variable.flag && (
                                    <LightTooltip
                                        title={tooltipContent.backend_variable.message}
                                        offset={[0, -12]}
                                        placement="bottom"
                                    >
                                        <WarningAmberIcon
                                            style={{
                                                fontWeight: "600",
                                                color: "#FF0000",
                                                fontSize: "16px",
                                                marginLeft: "3px",
                                            }}
                                        />
                                    </LightTooltip>
                                )}
                            </label>
                            <p className="text-xs">(White spaces are not allowed)</p>
                        </div>
                        <div className="col-8">
                            <input
                                type="text"
                                id="type"
                                value={data.backend_variable}
                                // disabled={type === "update"}
                                className="input-label-field input-label-field-width-500 text-sm"
                                onChange={(e) => handleInputValueChange(e.target.value, "backendVariableText", "backend_variable")}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    <div className="row pb-2">
                        <div className="col-4">
                            <label className="input-label vertical-center text-sm ">Claims Statement :</label>
                        </div>
                        <div className="col-8">
                            <input
                                type="text"
                                id="type"
                                value={data.statement}
                                className="input-label-field input-label-field-width-500 text-sm"
                                onChange={(e) => handleInputValueChange(e.target.value, "text", "statement")}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    <div className="row pb-2">
                        <div className="col-4">
                            <label className="input-label vertical-center text-sm ">Tags :</label>
                        </div>
                        <div className="col-8">
                            <Select
                                ref={selectInputRef}
                                className="text-sm input-label-select"
                                isMulti
                                isClearable={true}
                                isSearchable={true}
                                options={tagsData}
                                value={selectedTags}
                                onChange={(datum) => {
                                    handleInputValueChange(datum, "dropdown", "claims_tag_mapping");
                                    setSelectedTags(datum);
                                }}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        fontSize: "0.875rem",
                                        lineHeight: "1.25rem",
                                    }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>

                    <div className="row pb-2">
                        <div className="col-4">
                            <label className="input-label vertical-center text-sm ">
                                Likert Scale :
                                {likertWarning.flag && (
                                    <LightTooltip
                                        title={likertWarning.message}
                                        placement="right"
                                    >
                                        <WarningAmberIcon style={{ fontWeight: "600", color: "#FF0000", fontSize: "16px", marginLeft: "3px", cursor: "pointer" }} />
                                    </LightTooltip>
                                )}
                            </label>
                        </div>
                        <div className="col-8">
                            <input
                                type="text"
                                placeholder="Integer Only"
                                value={data.likert_scale}
                                className="input-label-field input-label-field-width-500 text-sm"
                                onChange={(e) => handleInputValueChange(e.target.value, "likertScaleText", "likert_scale")}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        className="custom-btn"
                        variant="outlined"
                        size="small"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                    <Button
                        className="custom-btn liner-light-blue-background me-3"
                        size="small"
                        autoFocus
                        variant="contained"
                        color="primary"
                        onClick={() => setIsConfirmationDialogOpen(true)}
                    >
                        {type === "add" ? "Add" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            {isConfirmationDialogOpen && (
                <Dialog
                    open={isConfirmationDialogOpen}
                    TransitionComponent={Transition}
                    keepMounted
                    // onClose={handleArchiveClickClose}
                    aria-describedby="alert-dialog-slide-description"
                >
                    <DialogTitle className="text-base">Confirm {type === "add" ? "adding" : "updating"} this data</DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            className="text-sm"
                            id="alert-dialog-slide-description"
                        >
                            Are you sure you want to {type === "add" ? "add" : "update"} this data?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            className="me-2 custom-btn"
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={handleConfirmationDialogClose}
                        >
                            Disagree
                        </Button>
                        <Button
                            className="me-2 liner-light-blue-background custom-btn"
                            size="small"
                            variant="contained"
                            onClick={handleSave}
                        >
                            Agree
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

// Typechecking props
StatementsAddUpdateComponent.propTypes = {
    title: PropTypes.string,
    type: PropTypes.string,
    tagsData: PropTypes.array,
    selectedRows: PropTypes.object,
    isOpen: PropTypes.bool,
    setIsAddDialogOpen: PropTypes.func,
    setIsUpdateDialogOpen: PropTypes.func,
    fetchData: PropTypes.func,
    dataView: PropTypes.object,
    selectedStudyType: PropTypes.object,
    selectedStudyTypeMethod: PropTypes.array,
};

export default StatementsAddUpdateComponent;
